import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faTriangleExclamation,
  faReceipt,
  faCircleCheck,
  faCircleHalfStroke,
  faCircleXmark,
  faGift,
  faScaleUnbalanced,
  faPlus,
  faPen,
  faTrashCan,
  faXmark,
  faUserPlus,
  faRotateRight,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import './Earnings.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const TABS = [
  ['summary', 'Summary'],
  ['paid', 'Paid sessions'],
  ['demo', 'Demo sessions'],
  ['incentive', 'Incentives'],
  ['bonus', 'Package bonuses'],
  ['penalties', 'Penalties'],
];

// Same pay codes as before
const PAY = {
  0: { label: 'Full pay', tone: 'full', icon: faCircleCheck, factor: 1 },
  1: { label: 'Half pay', tone: 'half', icon: faCircleHalfStroke, factor: 0.5 },
  2: { label: 'No pay', tone: 'none', icon: faCircleXmark, factor: 0 },
};
const payOf = (v) => PAY[v] || { label: 'Unknown', tone: 'none', icon: faCircleXmark, factor: 0 };

// Standard / Mastery class lines (older servers without classTypes: full / half pay as before)
const classRows = (data) => {
  const ct = data.classTypes;
  if (!ct) {
    return [
      { label: 'Paid classes · full pay', count: data.fullPayCount || 0, amount: data.fullPayTotal },
      { label: 'Paid classes · half pay', count: data.halfPayCount || 0, amount: data.halfPayTotal },
    ];
  }
  const rows = [];
  [
    ['standard', 'Standard'],
    ['mastery', 'Mastery'],
  ].forEach(([k, name]) => {
    const t = ct[k] || {};
    // the rate per class: "$3.5", or "$3.5–$4" if it changed during the month
    const money1 = (n) => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
    const rate =
      t.minRate == null ? null : t.minRate === t.maxRate ? money1(t.minRate) : `${money1(t.minRate)}–${money1(t.maxRate)}`;
    const halfRate =
      t.minRate == null ? null : t.minRate === t.maxRate ? money1(t.minRate / 2) : `${money1(t.minRate / 2)}–${money1(t.maxRate / 2)}`;
    if (t.fullCount || k === 'standard') rows.push({ label: `${name} classes · full pay`, count: t.fullCount || 0, rate, amount: t.fullTotal });
    if (t.halfCount) rows.push({ label: `${name} classes · half pay`, count: t.halfCount, rate: halfRate, amount: t.halfTotal });
  });
  return rows;
};
const typeOfClass = (d) => (d.ClassType || (Number(d.SessionRate) >= 5 ? 'mastery' : 'standard'));


const money = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const monthLabel = (y, m) => new Date(y, m, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const when = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { day: '—', time: '' };
  return {
    day: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
  };
};

// YYYY-MM-DD from the LOCAL date (toISOString() used UTC and could slip a day)
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayStr = () => ymd(new Date());

/* Same date the old page sent (today's day in the chosen month), from the local date */
const requestDate = (y, m) => {
  const lastDay = new Date(y, m + 1, 0).getDate();
  const day = Math.min(new Date().getDate(), lastDay);
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const Earnings = ({ userData }) => {
  const now = new Date();
  const [tab, setTab] = useState('summary');
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);

  const [form, setForm] = useState(null); // { id?, type, title, amount, date }
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // item
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const teacherId = userData?.id;
  const isCurrentMonth = ym.y === now.getFullYear() && ym.m === now.getMonth();

  const shiftMonth = (delta) => {
    setYm(({ y, m }) => {
      const d = new Date(y, m + delta, 1);
      const today = new Date();
      if (d.getFullYear() * 12 + d.getMonth() > today.getFullYear() * 12 + today.getMonth()) return { y, m }; // no future months
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  // ---------- Load (same endpoint) ----------
  useEffect(() => {
    if (!teacherId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await axios.get(`${API}/api/teacher/earnings/${teacherId}/${requestDate(ym.y, ym.m)}`, auth());
        if (!cancelled) setData(res.data || {});
      } catch (err) {
        console.error('Error fetching earnings:', err);
        if (!cancelled) {
          setData(null);
          setError('We couldn’t load the earnings for this month.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teacherId, ym, reload]);

  // ---------- Add / edit incentive or penalty (same endpoints + payloads) ----------
  const openAdd = (type) => {
    setForm({ type, title: '', amount: '', date: todayStr() });
    setFormError('');
  };

  const openEdit = (item) => {
    const d = new Date(item.DateOfIncentive);
    setForm({
      id: item.Id,
      type: item.Amount >= 0 ? 'incentive' : 'penalty',
      title: item.Title || '',
      amount: String(Math.abs(item.Amount)),
      date: Number.isNaN(d.getTime()) ? '' : String(item.DateOfIncentive).slice(0, 10),
    });
    setFormError('');
  };

  const onField = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setFormError('');
  };

  const saveForm = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setFormError('Title is required');
    if (!form.amount || parseFloat(form.amount) <= 0) return setFormError('Amount must be greater than 0');
    if (!form.date) return setFormError('Date is required');

    // penalties are saved as negative amounts (same as before)
    const amount = form.type === 'penalty' ? -Math.abs(parseFloat(form.amount)) : Math.abs(parseFloat(form.amount));
    setSaving(true);
    setFormError('');
    try {
      const res = form.id
        ? await axios.put(
            `${API}/api/teacher/earnings/update-incentive`,
            { Id: form.id, Title: form.title, Amount: amount, DateOfIncentive: form.date },
            auth()
          )
        : await axios.post(
            `${API}/api/teacher/earnings/add-incentive`,
            { TeacherId: teacherId, Title: form.title, Amount: amount, DateOfIncentive: form.date },
            auth()
          );
      if (res.data?.success) {
        setForm(null);
        setTab(form.type === 'penalty' ? 'penalties' : 'incentive');
        setReload((n) => n + 1);
      } else {
        setFormError(res.data?.error || `Failed to ${form.id ? 'update' : 'add'} ${form.type}`);
      }
    } catch (err) {
      console.error('Error saving incentive/penalty:', err);
      setFormError(err.response?.data?.error || `Failed to ${form.id ? 'update' : 'add'} ${form.type}`);
    } finally {
      setSaving(false);
    }
  };

  // ---------- Delete (same endpoint) ----------
  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const res = await axios.delete(`${API}/api/teacher/earnings/delete-incentive/${confirmDelete.Id}`, auth());
      if (res.data?.success) {
        setConfirmDelete(null);
        setReload((n) => n + 1);
      } else {
        setDeleteError('Failed to delete. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting incentive/penalty:', err);
      setDeleteError('Failed to delete. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // ---------- Package bonus: remove (refund, wrong case) or put back ----------
  const [bonusBusy, setBonusBusy] = useState(null); // payment id
  const [bonusError, setBonusError] = useState('');
  const toggleBonus = async (b) => {
    setBonusBusy(b.paymentId);
    setBonusError('');
    try {
      await axios.post(
        `${API}/api/teacher/earnings/bonus-exclude`,
        { IdPayment: b.paymentId, exclude: !b.excluded, Reason: b.excluded ? '' : 'Removed by admin' },
        auth()
      );
      setReload((n) => n + 1);
    } catch (err) {
      setBonusError(err.response?.data?.error || 'Couldn’t change this bonus. Please try again.');
    } finally {
      setBonusBusy(null);
    }
  };

  // change one bonus's amount (e.g. $2.50 each for siblings sharing a package); empty = back to the price list
  const editBonusAmount = async (b) => {
    const v = window.prompt(
      `Bonus for ${b.student} ($). Example: 2.5 for siblings sharing one package. Leave empty to go back to the normal amount.`,
      String(b.amount)
    );
    if (v === null) return;
    const amount = v.trim() === '' ? null : Number(v.replace(',', '.').replace('$', ''));
    if (amount !== null && (!Number.isFinite(amount) || amount < 0)) {
      setBonusError('Write an amount like 2.5');
      return;
    }
    setBonusBusy(b.paymentId);
    setBonusError('');
    try {
      await axios.post(`${API}/api/teacher/earnings/bonus-amount`, { IdPayment: b.paymentId, Amount: amount }, auth());
      setReload((n) => n + 1);
    } catch (err) {
      setBonusError(err.response?.data?.error || 'Couldn’t change this bonus. Please try again.');
    } finally {
      setBonusBusy(null);
    }
  };

  // Escape closes the open window
  useEffect(() => {
    if (!form && !confirmDelete) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (!saving) setForm(null);
      if (!deleting) setConfirmDelete(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [form, confirmDelete, saving, deleting]);

  // ---------- Derived lists ----------
  const paid = data?.paidDetails || [];
  const demos = data?.demoDetails || [];
  const extras = data?.incentiveData || [];
  const incentives = extras.filter((i) => i.Amount > 0);
  const penalties = extras.filter((i) => i.Amount < 0);
  const incentiveTotal = incentives.reduce((t, i) => t + i.Amount, 0);
  const penaltyTotal = penalties.reduce((t, i) => t + i.Amount, 0);
  // demo rate: from the server totals when possible (old page always showed $1.00)
  const demoRate = data?.demoCount ? (Number(data.demoPayTotal) || 0) / data.demoCount : 1;

  const bonuses = data?.packageBonusDetails || [];
  const counts = {
    paid: paid.length,
    demo: demos.length,
    incentive: incentives.length,
    bonus: bonuses.filter((b) => !b.excluded).length,
    penalties: penalties.length,
  };
  const showBonus = !!data?.packageBonusEnabled || Number(data?.packageBonus) > 0; // bonuses switched on (Prices & Offers)
  const tabs = TABS.filter(([k]) => k !== 'bonus' || showBonus);

  /* ---------------- Tab content ---------------- */
  const renderSummary = () => {
    const rows = [
      { label: 'Demo sessions', count: data.demoCount || 0, amount: data.demoPayTotal },
      ...classRows(data),
      { label: 'Incentives', amount: data.incentive },
      ...(showBonus ? [{ label: 'Package bonuses', count: data.packageBonusCount || 0, amount: data.packageBonus }] : []),
      { label: 'Arrears', amount: data.arrears },
      { label: 'Penalties', amount: data.penalties, negative: true },
    ];
    return (
      <ul className="rmaea-list rmaea-list--summary">
        {rows.map((r) => (
          <li key={r.label} className={`rmaea-row ${r.negative ? 'is-negative' : ''}`}>
            <span className="rmaea-row-main">{r.label}</span>
            <span className="rmaea-row-count">{r.count != null ? (r.rate ? `${r.count} × ${r.rate}` : `× ${r.count}`) : ''}</span>
            <span className="rmaea-amount">{money(r.amount)}</span>
          </li>
        ))}
        <li className="rmaea-row is-total">
          <span className="rmaea-row-main">Total earnings</span>
          <span className="rmaea-row-count" />
          <span className="rmaea-amount">{money(data.totalPay)}</span>
        </li>
      </ul>
    );
  };

  const renderSessions = (list, kind) => {
    if (!list.length) {
      return (
        <div className="rmaea-empty">
          <FontAwesomeIcon icon={faReceipt} />
          <p>{kind === 'paid' ? 'No paid classes recorded this month.' : 'No demo sessions this month.'}</p>
        </div>
      );
    }
    return (
      <ul className="rmaea-list">
        <li className="rmaea-row rmaea-row--head" aria-hidden="true">
          <span>Student</span>
          <span>Date</span>
          <span>Rate</span>
          <span>Status</span>
          <span className="rmaea-amount">Earned</span>
        </li>
        {list.map((d, i) => {
          const w = when(d.ClassDateTime);
          const pay = kind === 'paid' ? payOf(d.fullpay) : { label: 'Demo completed', tone: 'full', icon: faCircleCheck };
          const rate = kind === 'paid' ? Number(d.SessionRate) || 0 : demoRate;
          const earned = kind === 'paid' ? rate * pay.factor : demoRate;
          return (
            <li key={`${d.ClassDateTime}-${i}`} className="rmaea-row rmaea-row--session">
              <span className="rmaea-student">
                {d.fullname || 'Student'}
                {kind === 'paid' && (
                  <small className="rmaea-course">
                    <em className={`rmaea-type is-${typeOfClass(d)}`}>{typeOfClass(d) === 'mastery' ? 'Mastery' : 'Standard'}</em>
                    {d.Course || ''}
                  </small>
                )}
              </span>
              <span className="rmaea-when">
                {w.day}
                {w.time && <small>{w.time}</small>}
              </span>
              <span className="rmaea-rate">
                <small>Rate</small> {money(rate)}
              </span>
              <span className={`rmaea-pill is-${pay.tone}`}>
                <FontAwesomeIcon icon={pay.icon} /> {pay.label}
              </span>
              <span className="rmaea-amount">{money(earned)}</span>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderExtras = (list, kind) => {
    const total = kind === 'incentive' ? incentiveTotal : penaltyTotal;
    const addBtn = (
      <button type="button" className="rmaea-btn rmaea-btn--sm" onClick={() => openAdd(kind === 'incentive' ? 'incentive' : 'penalty')}>
        <FontAwesomeIcon icon={faPlus} /> Add {kind === 'incentive' ? 'incentive' : 'penalty'}
      </button>
    );
    if (!list.length) {
      return (
        <div className="rmaea-empty">
          <FontAwesomeIcon icon={kind === 'incentive' ? faGift : faCircleCheck} className={kind === 'penalties' ? 'is-good' : ''} />
          <p>{kind === 'incentive' ? 'No incentives this month.' : 'No penalties this month.'}</p>
          {addBtn}
        </div>
      );
    }
    return (
      <ul className="rmaea-list rmaea-list--summary">
        {list.map((item) => {
          const d = new Date(item.DateOfIncentive);
          return (
            <li key={item.Id} className={`rmaea-row rmaea-row--extra ${kind === 'penalties' ? 'is-negative' : 'is-positive'}`}>
              <span className="rmaea-row-main">
                <FontAwesomeIcon icon={kind === 'incentive' ? faGift : faScaleUnbalanced} className="rmaea-row-icon" />
                {item.Title || (kind === 'incentive' ? 'Incentive pay' : 'Penalty')}
              </span>
              <span className="rmaea-row-count">
                {Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
              <span className="rmaea-amount">{money(item.Amount)}</span>
              <span className="rmaea-actions">
                <button type="button" onClick={() => openEdit(item)} aria-label={`Edit ${item.Title || ''}`}>
                  <FontAwesomeIcon icon={faPen} />
                </button>
                <button
                  type="button"
                  className="is-danger"
                  onClick={() => {
                    setDeleteError('');
                    setConfirmDelete(item);
                  }}
                  aria-label={`Delete ${item.Title || ''}`}
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </button>
              </span>
            </li>
          );
        })}
        <li className={`rmaea-row rmaea-row--extra is-total ${kind === 'penalties' ? 'is-negative' : ''}`}>
          <span className="rmaea-row-main">Total</span>
          <span className="rmaea-row-count" />
          <span className="rmaea-amount">{money(total)}</span>
          <span className="rmaea-actions" />
        </li>
      </ul>
    );
  };

  const renderBonuses = () => {
    if (!bonuses.length) {
      return (
        <div className="rmaea-empty">
          <FontAwesomeIcon icon={faGift} />
          <p>No package bonuses this month. A bonus is added when a child this teacher demoed enrolls (and on renewals if they are turned on in Prices &amp; Offers).</p>
        </div>
      );
    }
    return (
      <>
        {bonusError && <p className="rmaea-error">{bonusError}</p>}
        {bonuses.some((b) => b.paid === false && !b.excluded) && (
          <p className="rmaea-hint">
            New students count only once their package is paid and they are enrolled (not pending). Renewals count when the
            package is recorded, paid or not: “Not paid yet” is only shown to you. If a family never pays, remove that bonus before
            paying the teacher.
          </p>
        )}
        <ul className="rmaea-list rmaea-list--summary">
          {bonuses.map((b) => {
            const d = new Date(b.paidAt);
            const what = `${b.sessions} ${b.type === 'mastery' ? 'Mastery' : 'Standard'}`;
            return (
              <li key={b.paymentId} className={`rmaea-row rmaea-row--extra ${b.excluded ? 'is-off' : 'is-positive'}`}>
                <span className="rmaea-row-main">
                  <FontAwesomeIcon icon={b.kind === 'new' ? faUserPlus : faRotateRight} className="rmaea-row-icon" />
                  {b.student} · {b.kind === 'new' ? `new student (${b.source === 'demo' ? 'her demo' : 'first class'})` : 'renewal'} · {what}
                  {b.paid === false && <em className="rmaea-unpaid">not paid yet</em>}
                  {b.excluded && b.reason && <em className="rmaea-unpaid">{b.reason}</em>}
                  {b.custom && !b.excluded && <em className="rmaea-unpaid">amount changed</em>}
                </span>
                <span className="rmaea-row-count">
                  {Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
                <span className="rmaea-amount">{b.excluded ? <s>{money(b.amount)}</s> : money(b.amount)}</span>
                <span className="rmaea-actions">
                  {!b.excluded && (
                    <button
                      type="button"
                      onClick={() => editBonusAmount(b)}
                      disabled={bonusBusy === b.paymentId}
                      aria-label={`Change the bonus for ${b.student}`}
                      title="Change the amount (e.g. $2.50 for siblings sharing a package)"
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                  )}
                  <button
                    type="button"
                    className={b.excluded ? '' : 'is-danger'}
                    onClick={() => toggleBonus(b)}
                    disabled={bonusBusy === b.paymentId}
                    aria-label={b.excluded ? `Put back the bonus for ${b.student}` : `Remove the bonus for ${b.student}`}
                    title={b.excluded ? 'Put back' : 'Remove (refund, wrong case)'}
                  >
                    <FontAwesomeIcon icon={b.excluded ? faRotateLeft : faXmark} />
                  </button>
                </span>
              </li>
            );
          })}
          <li className="rmaea-row rmaea-row--extra is-total">
            <span className="rmaea-row-main">Total</span>
            <span className="rmaea-row-count" />
            <span className="rmaea-amount">{money(data.packageBonus)}</span>
            <span className="rmaea-actions" />
          </li>
        </ul>
      </>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="rmaea-list rmaea-list--loading" aria-busy="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="rmaea-skel" />
          ))}
        </div>
      );
    }
    if (error || !data) {
      return (
        <div className="rmaea-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{error || 'No data for this month.'}</p>
          <button type="button" className="rmaea-btn" onClick={() => setReload((n) => n + 1)}>
            Try again
          </button>
        </div>
      );
    }
    if (tab === 'paid') return renderSessions(paid, 'paid');
    if (tab === 'demo') return renderSessions(demos, 'demo');
    if (tab === 'incentive') return renderExtras(incentives, 'incentive');
    if (tab === 'bonus' && showBonus) return renderBonuses();
    if (tab === 'penalties') return renderExtras(penalties, 'penalties');
    return renderSummary();
  };

  return (
    <div className="rmaea">
      {/* ---------- Month + admin actions ---------- */}
      <div className="rmaea-head">
        <div className="rmaea-month">
          <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month">
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <strong>{monthLabel(ym.y, ym.m)}</strong>
          <button type="button" onClick={() => shiftMonth(1)} disabled={isCurrentMonth} aria-label="Next month">
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
        <div className="rmaea-tools">
          <button type="button" className="rmaea-btn rmaea-btn--good" onClick={() => openAdd('incentive')}>
            <FontAwesomeIcon icon={faPlus} /> Incentive
          </button>
          <button type="button" className="rmaea-btn rmaea-btn--bad" onClick={() => openAdd('penalty')}>
            <FontAwesomeIcon icon={faPlus} /> Penalty
          </button>
        </div>
      </div>

      {/* ---------- Total card ---------- */}
      <section className="rmaea-hero" aria-label="Month total">
        <div className="rmaea-total">
          <span>{isCurrentMonth ? 'Earned so far this month' : `Total for ${monthLabel(ym.y, ym.m)}`}</span>
          <strong>{loading || !data ? '—' : money(data.totalPay)}</strong>
        </div>
        <ul className="rmaea-chips">
          <li>
            {!loading && data?.classTypes ? (
                <>
                  <b>{(data.classTypes.standard.fullCount || 0) + (data.classTypes.standard.halfCount || 0)}</b> Standard ·{' '}
                  <b>{(data.classTypes.mastery.fullCount || 0) + (data.classTypes.mastery.halfCount || 0)}</b> Mastery
                </>
              ) : (
                <>
                  <b>{loading || !data ? '–' : (data.fullPayCount || 0) + (data.halfPayCount || 0)}</b> paid classes
                </>
              )}
          </li>
          <li>
            <b>{loading || !data ? '–' : data.demoCount || 0}</b> demos
          </li>
          <li className="is-positive">
            <b>{loading || !data ? '–' : money(data.incentive)}</b> incentives
          </li>
          {showBonus && (
            <li className="is-positive">
              <b>{loading || !data ? '–' : money(data.packageBonus)}</b> bonuses
            </li>
          )}
          <li className="is-negative">
            <b>{loading || !data ? '–' : money(data.penalties)}</b> penalties
          </li>
        </ul>
      </section>

      {/* ---------- Tabs ---------- */}
      <div className="rmaea-tabs" role="tablist" aria-label="Earnings sections">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`rmaea-tab ${tab === key ? 'is-on' : ''}`}
            onClick={() => setTab(key)}
          >
            {label}
            {!loading && data && counts[key] > 0 && <span>{counts[key]}</span>}
          </button>
        ))}
      </div>

      <div className="rmaea-panel" role="tabpanel">
        {renderContent()}
      </div>

      {/* ---------- Add / edit window ---------- */}
      {form && (
        <div className="rmaea-overlay" onClick={() => !saving && setForm(null)}>
          <form className="rmaea-modal" role="dialog" aria-modal="true" aria-label="Incentive or penalty" onClick={(e) => e.stopPropagation()} onSubmit={saveForm} noValidate>
            <header className="rmaea-modal-head">
              <h2>
                {form.id ? 'Edit' : 'Add'} {form.type === 'incentive' ? 'incentive' : 'penalty'}
              </h2>
              <button type="button" className="rmaea-x" onClick={() => setForm(null)} disabled={saving} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </header>

            <div className="rmaea-modal-body">
              <div className="rmaea-type" role="radiogroup" aria-label="Type">
                {[
                  ['incentive', 'Incentive', faGift],
                  ['penalty', 'Penalty', faScaleUnbalanced],
                ].map(([v, label, icon]) => (
                  <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={form.type === v}
                    className={`${form.type === v ? 'is-on' : ''} ${v === 'incentive' ? 'is-good' : 'is-bad'}`}
                    onClick={() => setForm((f) => ({ ...f, type: v }))}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={icon} /> {label}
                  </button>
                ))}
              </div>

              <label className="rmaea-field">
                <span>Title</span>
                <input type="text" name="title" value={form.title} onChange={onField} placeholder="e.g. Extra demo weekend" maxLength={100} disabled={saving} autoFocus />
              </label>

              <div className="rmaea-field-row">
                <label className="rmaea-field">
                  <span>Amount (USD)</span>
                  <div className={`rmaea-money ${form.type === 'penalty' ? 'is-bad' : 'is-good'}`}>
                    <b>{form.type === 'penalty' ? '−$' : '+$'}</b>
                    <input type="number" name="amount" value={form.amount} onChange={onField} placeholder="0.00" min="0.01" step="0.01" inputMode="decimal" disabled={saving} />
                  </div>
                </label>
                <label className="rmaea-field">
                  <span>Date</span>
                  <input type="date" name="date" value={form.date} onChange={onField} max={todayStr()} disabled={saving} />
                </label>
              </div>

              <p className="rmaea-hint">
                {form.type === 'penalty' ? 'This amount will be deducted from the teacher’s earnings.' : 'This amount will be added to the teacher’s earnings.'}
              </p>
              {formError && <p className="rmaea-error">{formError}</p>}
            </div>

            <footer className="rmaea-modal-foot">
              <button type="button" className="rmaea-btn rmaea-btn--ghost" onClick={() => setForm(null)} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="rmaea-btn" disabled={saving}>
                {saving ? 'Saving…' : form.id ? 'Save changes' : `Add ${form.type}`}
              </button>
            </footer>
          </form>
        </div>
      )}

      {/* ---------- Delete confirm ---------- */}
      {confirmDelete && (
        <div className="rmaea-overlay" onClick={() => !deleting && setConfirmDelete(null)}>
          <div className="rmaea-dialog" role="alertdialog" aria-modal="true" aria-label="Delete" onClick={(e) => e.stopPropagation()}>
            <span className="rmaea-dialog-icon">
              <FontAwesomeIcon icon={faTrashCan} />
            </span>
            <h2>Delete this {confirmDelete.Amount < 0 ? 'penalty' : 'incentive'}?</h2>
            <p>
              <b>{confirmDelete.Title || 'Untitled'}</b> · {money(confirmDelete.Amount)}
            </p>
            {deleteError && <p className="rmaea-error">{deleteError}</p>}
            <div className="rmaea-dialog-actions">
              <button type="button" className="rmaea-btn rmaea-btn--ghost" onClick={() => setConfirmDelete(null)} disabled={deleting} autoFocus>
                Cancel
              </button>
              <button type="button" className="rmaea-btn rmaea-btn--danger" onClick={doDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Earnings;